import { CLASSIFICATION_TABLE, FAP_TABLE } from '../data/classification';
import { PAYS_UE, PAYS_GZALE } from '../data/countries';
export { classifyProduct } from './classifier';

// Legacy sync function — kept for backward compat, uses local matching only
export function identifierCodeSH(description) {
  const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const desc = norm(description);
  let best = null, bestScore = 0;

  for (const [key, entry] of Object.entries(CLASSIFICATION_TABLE)) {
    let score = 0;
    for (const mot of entry.mots_cles) {
      if (desc.includes(norm(mot))) score += mot.length;
    }
    if (score > bestScore) { bestScore = score; best = { key, ...entry }; }
  }

  if (!best || bestScore < 3) return null;
  return {
    categorie: best.key,
    code_sh: best.code_sh_10,
    codes_sh_chapitres: best.codes_sh,
    description: best.description,
    taux_DD: best.taux_DD,
    taux_TVA: best.taux_TVA,
    daps: best.daps,
    daps_taux: best.daps_taux,
    fap: best.fap,
    autorisation_prealable: best.autorisation_prealable || false,
    confiance: Math.min(100, bestScore * 5),
  };
}

export function getAccordPreferentiel(pays, code_sh, lang = 'fr') {
  const chapitre = parseInt((code_sh || '').substring(0, 2));
  const L = (fr, ar) => lang === 'ar' ? ar : fr;

  if (PAYS_UE.includes(pays)) {
    if (chapitre >= 25 && chapitre <= 97) {
      return { type: L('UE','الاتحاد الأوروبي'), taux_DD_pref: 0, document: 'EUR.1', eligible: true,
        note: L('Produit industriel éligible au taux préférentiel 0% DD (Accord d\'Association Algérie-UE). Le DAPS s\'applique même avec EUR.1.',
               'منتج صناعي مؤهل للمعدل التفضيلي 0% رسوم جمركية (اتفاق الشراكة الجزائر-الاتحاد الأوروبي). الرسم الإضافي المؤقت يُطبق حتى مع EUR.1.') };
    }
    return { type: L('UE','الاتحاد الأوروبي'), eligible: 'partiel', document: 'EUR.1',
      note: L('Produit agricole — vérifier contingents tarifaires spécifiques.',
             'منتج فلاحي — يجب التحقق من الحصص الجمركية المحددة.') };
  }

  if (PAYS_GZALE.includes(pays)) {
    return { type: L('GZALE — منطقة التبادل الحر العربية','منطقة التبادل الحر العربية الكبرى'), taux_DD_pref: 0,
      document: L('Certificat d\'origine arabe','شهادة المنشأ العربية'), eligible: true,
      note: L('Exonération DD si produit entièrement obtenu ou suffisamment transformé dans le pays.',
             'إعفاء من الرسوم الجمركية إذا كان المنتج محصلاً بالكامل أو محولاً بشكل كافٍ في البلد.') };
  }

  if (pays === 'TN') {
    return { type: L('Bilatéral Algérie-Tunisie','اتفاق ثنائي الجزائر-تونس'), eligible: 'partiel',
      document: L('Certificat d\'origine bilatéral','شهادة المنشأ الثنائية'),
      note: L('Réduction tarifaire sur certains produits — vérifier la liste.',
             'تخفيض جمركي على بعض المنتجات — يجب التحقق من القائمة.') };
  }

  return { type: L('Droit commun','النظام العام'), eligible: false,
    document: L('Certificat d\'origine standard','شهادة المنشأ العادية'),
    note: L('Aucun avantage tarifaire — taux plein applicable.',
           'لا توجد مزايا جمركية — المعدل الكامل مطبق.') };
}

export const DEFAULT_TC = { USD: 135, EUR: 148, GBP: 170, CNY: 18.7, DZD: 1 };

function _calcul(valeur_dzd, taux_DD, taux_TVA, daps_taux) {
  const DD = valeur_dzd * (taux_DD / 100);
  const TCS = valeur_dzd * 0.02;
  const DAPS = valeur_dzd * ((daps_taux || 0) / 100);
  const assiette_TVA = valeur_dzd + DD + TCS + DAPS;
  const TVA = assiette_TVA * (taux_TVA / 100);
  const total_taxes = DD + TCS + DAPS + TVA;
  return { DD: Math.round(DD), TCS: Math.round(TCS), DAPS: daps_taux > 0 ? Math.round(DAPS) : null, TVA: Math.round(TVA), total_taxes: Math.round(total_taxes), cout_total: Math.round(valeur_dzd + total_taxes) };
}

export function calculerDroits({ valeur, devise, incoterm, taux_DD, taux_TVA, daps_taux, pays, accord, codeActivite, tauxChange }) {
  const CIF_FACTORS = { CIF: 1, CIP: 1, CFR: 1.02, CPT: 1.02, FOB: 1.12, FCA: 1.12, FAS: 1.14, EXW: 1.18, DAP: 0.95, DPU: 0.95, DDP: 0.90 };
  let valeur_cif = valeur * (CIF_FACTORS[incoterm] || 1);

  const taux = tauxChange || DEFAULT_TC[devise] || DEFAULT_TC.USD;
  const valeur_dzd = valeur_cif * taux;

  let dd_rate = taux_DD;
  if (accord?.eligible === true && accord.taux_DD_pref !== undefined) dd_rate = accord.taux_DD_pref;

  const principal = _calcul(valeur_dzd, dd_rate, taux_TVA, daps_taux);

  // Comparateur : calcul sans accord (taux plein)
  let comparaison = null;
  if (dd_rate !== taux_DD) {
    comparaison = _calcul(valeur_dzd, taux_DD, taux_TVA, daps_taux);
    comparaison.economie = comparaison.total_taxes - principal.total_taxes;
  }

  // TDB
  let tdb = null;
  if (codeActivite === '04') {
    tdb = { taux: 0.3, montant: Math.max(20000, Math.round(valeur_dzd * 0.003)), label: 'TDB biens revente (0.3%, min 20 000 DZD)' };
  } else if (codeActivite === '07') {
    tdb = { taux: 4, montant: Math.round(valeur_dzd * 0.04), label: 'TDB services (4%)' };
  }
  // code 01 industrie : exonéré TDB pour biens d'équipement/matières premières hors revente

  // Economy EUR.1
  let economie_eur1 = null;
  if (PAYS_UE.includes(pays) && taux_DD > 0) {
    economie_eur1 = Math.round(valeur_dzd * (taux_DD / 100));
  }

  return {
    valeur_cif_dzd: Math.round(valeur_dzd),
    taux_DD_applique: dd_rate,
    ...principal,
    daps_taux,
    comparaison,
    tdb,
    economie_eur1,
    taux_change: taux,
    devise,
  };
}

export function getDocumentsObligatoires(categorie, pays, codeActivite, lang = 'fr') {
  const C = require('../data/content').CONTENT[lang] || require('../data/content').CONTENT.fr;
  const getFap = require('../data/content').getFapContent;

  const docs_fournisseur = [
    { id: 'facture', nom: C.doc_facture, obligatoire: true, details: C.doc_facture_details },
    { id: 'packing', nom: C.doc_packing, obligatoire: true, details: C.doc_packing_details },
    { id: 'bl', nom: C.doc_bl, obligatoire: true, details: C.doc_bl_details },
  ];

  if (PAYS_UE.includes(pays)) {
    docs_fournisseur.push({ id: 'eur1', nom: C.doc_eur1, obligatoire: false, recommande: true, details: C.doc_eur1_details });
  } else if (PAYS_GZALE.includes(pays)) {
    docs_fournisseur.push({ id: 'cert_arabe', nom: C.doc_cert_arabe, obligatoire: true, details: C.doc_cert_arabe_details });
  } else {
    docs_fournisseur.push({ id: 'cert_standard', nom: C.doc_cert_standard, obligatoire: true, details: C.doc_cert_standard_details });
  }

  const entry = CLASSIFICATION_TABLE[categorie];
  if (entry) {
    for (const fap of (entry.fap || [])) {
      const f = getFap(fap, lang);
      if (f) {
        docs_fournisseur.push({ id: fap, nom: f.nom, obligatoire: true,
          details: `${f.description}. ${lang === 'ar' ? 'الهيئة' : 'Organisme'} : ${f.organisme}. ${lang === 'ar' ? 'المدة' : 'Délai'} : ${f.delai}.` });
      }
    }
  }

  if (entry?.fap?.includes('C.C')) {
    docs_fournisseur.push({ id: 'fiche_tech', nom: C.doc_fiche_tech, obligatoire: true, details: C.doc_fiche_tech_details });
  }

  const docs_importateur = [
    { id: 'ppi', nom: C.doc_ppi, obligatoire: true, details: C.doc_ppi_details },
    { id: 'rc', nom: C.doc_rc, obligatoire: true, details: C.doc_rc_details },
    { id: 'domiciliation', nom: C.doc_domiciliation, obligatoire: true, details: C.doc_domiciliation_details },
  ];

  if (codeActivite === '07') {
    docs_importateur.push({ id: 'tdb', nom: C.doc_tdb_services, obligatoire: true, details: C.doc_tdb_services_details });
  } else if (codeActivite === '04') {
    docs_importateur.push({ id: 'tdb', nom: C.doc_tdb_negoce, obligatoire: true, details: C.doc_tdb_negoce_details });
  }

  if (categorie === 'cosmetiques') {
    docs_importateur.push({ id: 'auto_cosm', nom: C.doc_auto_cosm, obligatoire: true, details: C.doc_auto_cosm_details });
  }

  return { docs_fournisseur, docs_importateur };
}

export function detecterEconomies(code_sh, pays, calcul) {
  const alertes = [];
  if (PAYS_UE.includes(pays) && calcul?.economie_eur1 && calcul.taux_DD_applique > 0) {
    alertes.push({
      type: 'EUR.1',
      message: `EUR.1 non utilisé ! Économie potentielle : ${calcul.economie_eur1.toLocaleString('fr-DZ')} DZD`,
      action: 'Demandez le certificat EUR.1 à votre fournisseur européen avant l\'expédition.',
    });
  }
  if (PAYS_UE.includes(pays) && calcul?.comparaison) {
    alertes.push({
      type: 'COMPARAISON',
      message: `Avec EUR.1 vous économisez ${calcul.comparaison.economie.toLocaleString('fr-DZ')} DZD sur les taxes totales.`,
      action: 'Le certificat EUR.1 réduit les DD à 0% pour les produits industriels UE.',
    });
  }
  if (PAYS_GZALE.includes(pays)) {
    alertes.push({
      type: 'GZALE',
      message: 'Certificat d\'origine arabe disponible — exonération DD possible.',
      action: 'Demandez le certificat d\'origine GZALE à la Chambre de Commerce du pays.',
    });
  }
  return alertes;
}
